/**
 * Router admin — React Router, code-splitting (route-level lazy) + errorElement.
 * US-AE.F0-0000-S06: wire TOÀN BỘ route theo nav.ts (IA design). Mỗi route bọc
 * withShell (lazy + RequireAuth + AppShell). Mọi màn (S10–S63) đã port xong (wave 1–5):
 * route = page riêng (board/form/detail/2pane) hoặc ResourceListPage (list config-driven).
 *
 * Bundle ban đầu nhỏ: mỗi page tải theo nhu cầu (performance.md bundle budget).
 */
import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter } from "react-router-dom";
import { AppShell } from "../components/layout/AppShell.js";
import { RouteError } from "../components/feedback/RouteError.js";
import { NotFound } from "../components/feedback/NotFound.js";
import { RouteFallback } from "../components/feedback/RouteFallback.js";
import { RequireAuth } from "./RequireAuth.js";

// ── Pages đã build (lazy, named→default cho React.lazy) ──
const DashboardPage = lazy(() =>
  import("../features/dashboard/DashboardPage.js").then((m) => ({ default: m.DashboardPage })),
);
const SystemSettingsPage = lazy(() =>
  import("../features/system-settings/SystemSettingsPage.js").then((m) => ({
    default: m.SystemSettingsPage,
  })),
);
const RbacMatrixPage = lazy(() =>
  import("../features/rbac-matrix/RbacMatrixPage.js").then((m) => ({
    default: m.RbacMatrixPage,
  })),
);
const MembersPage = lazy(() =>
  import("../features/members/MembersPage.js").then((m) => ({ default: m.MembersPage })),
);
// US-AE-IMP.F4-0114 (@UC9144) — trình quản lý allowlist domain admin (thay sửa JSON thô).
const AllowlistPage = lazy(() =>
  import("../features/allowlist/AllowlistPage.js").then((m) => ({ default: m.AllowlistPage })),
);
const MoneyPolicyPage = lazy(() =>
  import("../features/money-policy/MoneyPolicyPage.js").then((m) => ({ default: m.MoneyPolicyPage })),
);
const SearchPage = lazy(() =>
  import("../features/search/SearchPage.js").then((m) => ({ default: m.SearchPage })),
);
const UsersPage = lazy(() =>
  import("../features/users/UsersPage.js").then((m) => ({ default: m.UsersPage })),
);
const UserDetailPage = lazy(() =>
  import("../features/users/UserDetailPage.js").then((m) => ({ default: m.UserDetailPage })),
);
// US-AE11.F1-0038 (@UC9100/@UC9101) — chi tiết case tranh chấp tài chính (linked context read-only).
const DisputeDetailPage = lazy(() =>
  import("../features/disputes/DisputeDetailPage.js").then((m) => ({ default: m.DisputeDetailPage })),
);
// US-AE-IMP2-T11 (@UC9154/@UC9155/@UC9156) — detail read-only đợt thu / phiên biểu quyết / truy vết AI.
// 🔴 US-AE16.F2-0370-S03 (@UC9635 @UC9636) — VAN TAY dừng máy đẻ nghĩa vụ tiền định kỳ.
const SchedulesPage = lazy(() =>
  import("../features/collection-schedules/SchedulesPage.js").then((m) => ({
    default: m.SchedulesPage,
  })),
);
const CollectionDetailPage = lazy(() =>
  import("../features/collections-oversight/CollectionDetailPage.js").then((m) => ({
    default: m.CollectionDetailPage,
  })),
);
const VotingDetailPage = lazy(() =>
  import("../features/voting-oversight/VotingDetailPage.js").then((m) => ({
    default: m.VotingDetailPage,
  })),
);
// US-AE5.F6-0093 (@UC9164) — danh sách TOÀN BỘ khoản chi xuyên quỹ (read-only oversight, filter server-side + full-text).
const ExpenseDetailPage = lazy(() =>
  import("../features/expenses-oversight/ExpenseDetailPage.js").then((m) => ({
    default: m.ExpenseDetailPage,
  })),
);
const ExpensesPage = lazy(() =>
  import("../features/expenses-oversight/ExpensesPage.js").then((m) => ({
    default: m.ExpensesPage,
  })),
);
// US-E21.F7-0219 (@UC9202–@UC9204) — chi tiết chia suất 1 khoản chi + can thiệp (REVERSAL+repost).
const ExpenseSplitsPage = lazy(() =>
  import("../features/expense-splits/ExpenseSplitsPage.js").then((m) => ({
    default: m.ExpenseSplitsPage,
  })),
);
// US-E20.F6-0212 (@UC9343–@UC9345) — giám sát + can thiệp 5 cỗ máy tài chính chuyên biệt.
const MachinesPage = lazy(() =>
  import("../features/admin-machines/MachinesPage.js").then((m) => ({
    default: m.MachinesPage,
  })),
);
const DebtsPage = lazy(() =>
  import("../features/debts-oversight/DebtsPage.js").then((m) => ({
    default: m.DebtsPage,
  })),
);
const AiAuditTraceDetailPage = lazy(() =>
  import("../features/ai-audit/AiAuditTraceDetailPage.js").then((m) => ({
    default: m.AiAuditTraceDetailPage,
  })),
);
const CorrectionDetailPage = lazy(() =>
  import("../features/corrections/CorrectionDetailPage.js").then((m) => ({
    default: m.CorrectionDetailPage,
  })),
);
// 🔴 US-E19.F7-0206 (@UC9320) — bảng điều khiển an ninh (read tách can-thiệp).
const SecurityConsolePage = lazy(() =>
  import("../features/security-console/SecurityConsolePage.js").then((m) => ({
    default: m.SecurityConsolePage,
  })),
);
const IdentityLookupPage = lazy(() =>
  import("../features/identity/IdentityLookupPage.js").then((m) => ({
    default: m.IdentityLookupPage,
  })),
);
const LocalePage = lazy(() =>
  import("../features/locale/LocalePage.js").then((m) => ({ default: m.LocalePage })),
);
const FeatureFlagsPage = lazy(() =>
  import("../features/feature-flags/FeatureFlagsPage.js").then((m) => ({
    default: m.FeatureFlagsPage,
  })),
);
const ConfigOverridesPage = lazy(() =>
  import("../features/config-overrides/ConfigOverridesPage.js").then((m) => ({
    default: m.ConfigOverridesPage,
  })),
);
const GroupsPage = lazy(() =>
  import("../features/groups/GroupsPage.js").then((m) => ({ default: m.GroupsPage })),
);
const GroupDetailPage = lazy(() =>
  import("../features/groups/GroupDetailPage.js").then((m) => ({ default: m.GroupDetailPage })),
);
// US-AE4.F7-1028 (@UC9952) — trang Chi tiết của một suất (mở từ ngăn Chi tiết suất).
const MemberContributionsPage = lazy(() =>
  import("../features/groups/MemberContributionsPage.js").then((m) => ({ default: m.MemberContributionsPage })),
);
const FundsPage = lazy(() =>
  import("../features/funds/FundsPage.js").then((m) => ({ default: m.FundsPage })),
);
const FundDetailPage = lazy(() =>
  import("../features/funds/FundDetailPage.js").then((m) => ({ default: m.FundDetailPage })),
);
const MemberImportPage = lazy(() =>
  import("../features/member-import/MemberImportPage.js").then((m) => ({
    default: m.MemberImportPage,
  })),
);
const ReconcilePage = lazy(() =>
  import("../features/reconcile/ReconcilePage.js").then((m) => ({ default: m.ReconcilePage })),
);
const DriftMonitorPage = lazy(() =>
  import("../features/drift/DriftMonitorPage.js").then((m) => ({ default: m.DriftMonitorPage })),
);
const CashflowPage = lazy(() =>
  import("../features/cashflow/CashflowPage.js").then((m) => ({ default: m.CashflowPage })),
);
const IdempotencyPage = lazy(() =>
  import("../features/idempotency/IdempotencyPage.js").then((m) => ({ default: m.IdempotencyPage })),
);
const InboxPage = lazy(() =>
  import("../features/inbox/InboxPage.js").then((m) => ({ default: m.InboxPage })),
);
// US-AE11.F4-1026 (@UC9105) — yêu cầu hỗ trợ gửi từ app. `/:id` = link mở thẳng từ email báo admin.
const SupportRequestsPage = lazy(() =>
  import("../features/support-requests/SupportRequestsPage.js").then((m) => ({ default: m.SupportRequestsPage })),
);
const PaymentsPage = lazy(() =>
  import("../features/payments/PaymentsPage.js").then((m) => ({ default: m.PaymentsPage })),
);
const OutboxPage = lazy(() =>
  import("../features/outbox/OutboxPage.js").then((m) => ({ default: m.OutboxPage })),
);
const ChannelsPage = lazy(() =>
  import("../features/channels/ChannelsPage.js").then((m) => ({ default: m.ChannelsPage })),
);
const BroadcastPage = lazy(() =>
  import("../features/broadcast/BroadcastPage.js").then((m) => ({ default: m.BroadcastPage })),
);
// US-AE13.F2-0045 (@UC9122) — cấu hình khung giờ yên lặng VN (hoãn push/ZNS).
const QuietHoursPanel = lazy(() =>
  import("../features/transparency-ops/QuietHoursPanel.js").then((m) => ({
    default: m.QuietHoursPanel,
  })),
);
const MetricsPage = lazy(() =>
  import("../features/metrics/MetricsPage.js").then((m) => ({ default: m.MetricsPage })),
);
const BackupPage = lazy(() =>
  import("../features/backup/BackupPage.js").then((m) => ({ default: m.BackupPage })),
);
const AiCostPage = lazy(() =>
  import("../features/ai-cost/AiCostPage.js").then((m) => ({ default: m.AiCostPage })),
);
const AiEvalPage = lazy(() =>
  import("../features/ai-eval/AiEvalPage.js").then((m) => ({ default: m.AiEvalPage })),
);
const AiModelsPage = lazy(() =>
  import("../features/ai-models/AiModelsPage.js").then((m) => ({ default: m.AiModelsPage })),
);
// US-AIV2-0071 (@UC0150) — Sổ năng lực trợ lý v2, CHỈ ĐỌC. Thay 3 màn AI v1 đã xoá ở 0002;
// path MỚI (`/ai/registry`) vì `router.sunset-ai-v1.test.tsx` cấm `/ai/orchestration` quay lại.
const AiRegistryPage = lazy(() =>
  import("../features/ai-registry/AiRegistryPage.js").then((m) => ({ default: m.AiRegistryPage })),
);
// US-AIF.P0-0245 (@UC9417) — kill-switch AI ghi tiền: gom 3 tầng (env + flag + group) read-only.
const AiKillSwitchPage = lazy(() =>
  import("../features/ai-killswitch/AiKillSwitchPage.js").then((m) => ({
    default: m.AiKillSwitchPage,
  })),
);
const OnboardingPage = lazy(() =>
  import("../features/onboarding/OnboardingPage.js").then((m) => ({ default: m.OnboardingPage })),
);
const AnalyticsGrowthPage = lazy(() =>
  import("../features/analytics-growth/AnalyticsGrowthPage.js").then((m) => ({
    default: m.AnalyticsGrowthPage,
  })),
);
const AnalyticsReferralsPage = lazy(() =>
  import("../features/analytics-referrals/AnalyticsReferralsPage.js").then((m) => ({
    default: m.AnalyticsReferralsPage,
  })),
);
const AnalyticsGroupHealthPage = lazy(() =>
  import("../features/analytics-group-health/AnalyticsGroupHealthPage.js").then((m) => ({
    default: m.AnalyticsGroupHealthPage,
  })),
);
const CiOversightPage = lazy(() =>
  import("../features/ci-oversight/CiOversightPage.js").then((m) => ({
    default: m.CiOversightPage,
  })),
);
// US-AE14.F1-0046 (@UC9130 plan-catalog · @UC9132 entitlement) — danh mục gói & subscription.
const BillingPage = lazy(() =>
  import("../features/billing/BillingPage.js").then((m) => ({ default: m.BillingPage })),
);
// US-AE14.F2-0047 (@UC9131 usage-metering · @UC9133 unit-economics) — read-only metering & economics.
const UsageEconomicsPage = lazy(() =>
  import("../features/billing/UsageEconomicsPage.js").then((m) => ({
    default: m.UsageEconomicsPage,
  })),
);
const LoginPage = lazy(() =>
  import("../features/auth/LoginPage.js").then((m) => ({ default: m.LoginPage })),
);
// US-AE-IMP.F3 (@UC9141) — trang CÔNG KHAI quên/đặt lại mật khẩu (link email reset admin trỏ về đây).
const ResetPasswordPage = lazy(() =>
  import("../features/auth/ResetPasswordPage.js").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
// US-AE-IMP.F3-0143 (@UC9150) — trang CÔNG KHAI thiết lập 2FA sau login (khi requiresEnrollment).
const EnrollPage = lazy(() =>
  import("../features/auth/EnrollPage.js").then((m) => ({ default: m.EnrollPage })),
);
// US-AE-IMP.F3-0144 (@UC9151) — trang CÔNG KHAI đặt mật khẩu lần đầu cho admin mới được mời.
const AcceptInvitePage = lazy(() =>
  import("../features/auth/AcceptInvitePage.js").then((m) => ({ default: m.AcceptInvitePage })),
);
// US-AE-IMP.F3-0112 (@UC9142) — trang tài khoản self-service (2FA + đổi mật khẩu + phiên).
const AccountPage = lazy(() =>
  import("../features/account/AccountPage.js").then((m) => ({ default: m.AccountPage })),
);

// ── Engine dùng chung (list config-driven) ──
// Mọi màn (S10–S63) đã port xong (wave 1–5) → không còn StubPage nào ở router.
// StubPage component vẫn giữ trong features/common (infra + test) cho tương lai nếu cần.
const ResourceListPage = lazy(() =>
  import("../features/resource/ResourceListPage.js").then((m) => ({ default: m.ResourceListPage })),
);

function withShell(node: ReactNode) {
  return (
    <RequireAuth>
      <AppShell>
        <Suspense fallback={<RouteFallback />}>{node}</Suspense>
      </AppShell>
    </RequireAuth>
  );
}
function withoutShell(node: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>;
}

/** Route dùng ResourceList (có config trong resources.ts). */
function list(resourceKey: string): ReactNode {
  return <ResourceListPage resourceKey={resourceKey} />;
}
/** Định nghĩa route: path → element (page thật hoặc list engine — không còn stub). */
const ROUTES: { path: string; element: ReactNode }[] = [
  // Tổng quan
  { path: "/", element: <DashboardPage /> },
  // AE1 — Truy cập & quản trị
  { path: "/admins", element: <MembersPage /> },
  // US-AE-IMP.F4-0114 (@UC9144) — allowlist domain admin (panel chuyên dụng).
  { path: "/allowlist", element: <AllowlistPage /> },
  { path: "/audit-log", element: list("auditlog") },
  // AE2 — Cấu hình hệ thống
  { path: "/settings", element: <SystemSettingsPage /> },
  { path: "/rbac-matrix", element: <RbacMatrixPage /> },
  { path: "/flags", element: <FeatureFlagsPage /> },
  { path: "/locale", element: <LocalePage /> },
  { path: "/config-overrides", element: <ConfigOverridesPage /> },
  // AE3 — Người dùng & định danh
  { path: "/users", element: <UsersPage /> },
  { path: "/users/:id", element: <UserDetailPage /> },
  { path: "/gdpr", element: list("gdpr") },
  { path: "/identity", element: <IdentityLookupPage /> },
  // AE4 — Nhóm & quỹ
  { path: "/groups", element: <GroupsPage /> },
  { path: "/groups/:id", element: <GroupDetailPage /> },
  { path: "/groups/:id/members/:memberId", element: <MemberContributionsPage /> },
  { path: "/funds", element: <FundsPage /> },
  { path: "/funds/:id", element: <FundDetailPage /> },
  // US-AE-IMP2-T14 (@UC9162) — Admin bulk member-import Excel/CSV.
  { path: "/member-imports", element: <MemberImportPage /> },
  // AE5 — Toàn vẹn tài chính
  { path: "/reconcile", element: <ReconcilePage /> },
  { path: "/drift", element: <DriftMonitorPage /> },
  { path: "/cashflow", element: <CashflowPage /> },
  { path: "/idempotency", element: <IdempotencyPage /> },
  // AE6 — Thanh toán & Cổng
  { path: "/payments", element: <PaymentsPage /> },
  // AE10 — Đợt thu & chi
  { path: "/collections", element: list("collections") },
  // 🔴 US-AE16.F2-0370-S03 (@UC9635 @UC9636) — VAN TAY dừng máy đẻ nghĩa vụ tiền định kỳ.
  { path: "/collection-schedules", element: <SchedulesPage /> },
  { path: "/collections/:id", element: <CollectionDetailPage /> },
  { path: "/expenses", element: <ExpensesPage /> },
  // US-AE11.F6-0848 (@UC9651) — chi tiết 1 khoản chi (xem + sửa + lịch sử). Đặt cạnh route
  // `/splits` đã có để hai lối vào của cùng một khoản chi nằm cùng chỗ khi đọc.
  { path: "/expenses/:id", element: <ExpenseDetailPage /> },
  { path: "/expenses/:id/splits", element: <ExpenseSplitsPage /> },
  { path: "/debts", element: <DebtsPage /> },
  { path: "/machines", element: <MachinesPage /> },
  { path: "/voting", element: list("voting") },
  { path: "/voting/:id", element: <VotingDetailPage /> },
  // AE11 — Hỗ trợ & tranh chấp
  { path: "/disputes", element: list("disputes") },
  { path: "/disputes/:id", element: <DisputeDetailPage /> },
  { path: "/corrections", element: list("corrections") },
  { path: "/corrections/:id", element: <CorrectionDetailPage /> },
  { path: "/inbox", element: <InboxPage /> },
  { path: "/support-requests", element: <SupportRequestsPage /> },
  { path: "/support-requests/:id", element: <SupportRequestsPage /> },
  // AE12 — An toàn & chống gian lận
  { path: "/risk", element: list("risk") },
  { path: "/abuse", element: list("abuse") },
  { path: "/moderation", element: list("moderation") },
  // AE12b — Tăng trưởng & minh bạch
  { path: "/transparency", element: list("transparency") },
  { path: "/referrals", element: list("referrals") },
  { path: "/onboarding", element: <OnboardingPage /> },
  { path: "/analytics/growth", element: <AnalyticsGrowthPage /> },
  { path: "/analytics/referrals", element: <AnalyticsReferralsPage /> },
  { path: "/analytics/group-health", element: <AnalyticsGroupHealthPage /> },
  // AE13 — Truyền thông
  { path: "/broadcast", element: <BroadcastPage /> },
  { path: "/quiet-hours", element: <QuietHoursPanel /> },
  // AE7 — Thông báo
  { path: "/outbox", element: <OutboxPage /> },
  { path: "/channels", element: <ChannelsPage /> },
  { path: "/templates", element: list("templates") },
  // AE8/AE14 — Vận hành AI
  // US-AI.F5-0234 (@UC9400 @AI-TRU-01): MỞ LẠI route — backend config flow + preview engine đã có
  // ⇒ trang wire THẬT (PATCH + preview chạy engine trên sandbox), không còn mock của F8.
  // US-AIF.P0-0245 (@UC9417) — kill-switch AI ghi tiền (gom env + flag + group; đọc thật DP-5).
  { path: "/ai/kill-switch", element: <AiKillSwitchPage /> },
  { path: "/ai/models", element: <AiModelsPage /> },
  { path: "/ai/registry", element: <AiRegistryPage /> },
  { path: "/ai/audit", element: list("ai_audit") },
  { path: "/ai/audit/:id", element: <AiAuditTraceDetailPage /> },
  { path: "/ai/cost", element: <AiCostPage /> },
  { path: "/ai/eval", element: <AiEvalPage /> },
  { path: "/ai/safety", element: list("ai_safety") },
  // US-AI-0310-S06 (@UC9400 · @gate config) — config deep-link AI theo iOS/Android/Web (5 field).
  // AE14 — Monetization (danh mục gói & subscription nhóm)
  { path: "/billing", element: <BillingPage /> },
  // AE14 — Usage metering & unit economics (read-only)
  { path: "/billing/usage", element: <UsageEconomicsPage /> },
  // AE10 — CI/CD & SDLC gates (giám sát pipeline & human gate — read-only)
  { path: "/ci", element: <CiOversightPage /> },
  // AE9 — Giám sát & bảo mật
  { path: "/metrics", element: <MetricsPage /> },
  { path: "/alerts", element: list("alerts") },
  { path: "/threats", element: list("threats") },
  // 🔴 US-E19.F7-0206 (@UC9320) — console an ninh: read (freeze/anomaly/cooling/audit) TÁCH can-thiệp.
  { path: "/security-console", element: <SecurityConsolePage /> },
  { path: "/audit-viewer", element: list("auditlog") },
  { path: "/backup", element: <BackupPage /> },
  { path: "/secrets", element: list("secrets") },
  // Tài khoản self-service (2FA + đổi mật khẩu + phiên) — @UC9142.
  { path: "/account", element: <AccountPage /> },
  // Tìm kiếm
  { path: "/search", element: <SearchPage /> },
  // Master Data / Danh mục
  { path: "/dict/income-types", element: list("income_types") },
  { path: "/dict/expense-categories", element: list("expense_categories") },
  { path: "/dict/chart-of-accounts", element: list("chart_of_accounts") },
  { path: "/dict/enum-registry", element: list("enum_registry") },
  { path: "/dict/error-catalog", element: list("error_catalog") },
  { path: "/dict/blocklist", element: list("blocklist") },
  { path: "/dict/reason-codes", element: list("reason_codes") },
  { path: "/dict/referral-channels", element: list("referral_channels") },
  { path: "/dict/banks", element: list("banks") },
  // Route giữ tương thích (page cũ ngoài IA design)
  { path: "/money-policy", element: <MoneyPolicyPage /> },
];

export const router = createBrowserRouter([
  {
    path: "/login",
    element: withoutShell(<LoginPage />),
    errorElement: <RouteError />,
  },
  {
    // @UC9141 — CÔNG KHAI (ngoài AppShell/RequireAuth): request email + confirm token (?token=).
    path: "/reset-password",
    element: withoutShell(<ResetPasswordPage />),
    errorElement: <RouteError />,
  },
  {
    // @UC9150 — CÔNG KHAI (ngoài AppShell/RequireAuth): thiết lập 2FA sau login khi chưa có TOTP.
    // enrollTicket đến qua router state từ LoginPage (in-memory, không cần session/RequireAuth).
    path: "/enroll",
    element: withoutShell(<EnrollPage />),
    errorElement: <RouteError />,
  },
  {
    // @UC9151 — CÔNG KHAI (ngoài AppShell/RequireAuth): đặt mật khẩu lần đầu admin mới (?token=).
    path: "/accept-invite",
    element: withoutShell(<AcceptInvitePage />),
    errorElement: <RouteError />,
  },
  ...ROUTES.map((r) => ({
    path: r.path,
    element: withShell(r.element),
    errorElement: <RouteError />,
  })),
  {
    path: "*",
    element: withShell(<NotFound />),
    errorElement: <RouteError />,
  },
]);
